import { Link } from "react-router-dom";
import { ArrowRight, PoundSterling, FileCheck, Wallet, Repeat } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/content";

const grants = [
  { name: "Boiler Upgrade Scheme (BUS)", value: "Up to £7,500", desc: "Government grant toward the cost of installing an air source heat pump, replacing fossil-fuel heating." },
  { name: "ECO4", value: "Fully funded*", desc: "Support for eligible households (income or benefits based) covering insulation, heating and more." },
  { name: "Great British Insulation Scheme", value: "Insulation funding", desc: "Help with insulation measures for homes in lower council tax bands with poorer energy ratings." },
];

export default function GrantsFinance() {
  return (
    <div data-testid="grants-page">
      <PageHeader
        overline="Grants & Finance"
        title="How you pay nothing upfront"
        subtitle="We combine government grants with flexible finance so the right measures fit your budget — often with no deposit at all."
      />

      {/* Grants */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-3">Government grants we work with</h2>
            <p className="font-figtree text-lg text-stone-600 max-w-2xl mb-12">Eligibility depends on your property and circumstances — we check everything for you during your free survey.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {grants.map((g, i) => (
              <Reveal key={g.name} delay={i * 0.08}>
                <div className="bg-white border border-stone-200 rounded-2xl p-8 h-full" data-testid={`grant-card-${i}`}>
                  <PoundSterling className="w-8 h-8 text-brand-accent mb-4" />
                  <p className="font-outfit text-2xl font-bold text-brand">{g.value}</p>
                  <h3 className="font-outfit text-lg font-medium text-brand mt-2 mb-3">{g.name}</h3>
                  <p className="font-figtree text-sm text-stone-600">{g.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Finance split */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="rounded-3xl overflow-hidden">
            <img src={IMAGES.finance} alt="Planning finance" className="w-full h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">Subscription-style finance</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-5">Spread the cost, skip the deposit</h2>
            <div className="space-y-6">
              {[
                [Wallet, "No large upfront payment", "Where a grant doesn't fully cover a measure, finance lets you avoid a big bill on day one."],
                [Repeat, "Predictable monthly payments", "A simple monthly plan — like a subscription for your home's efficiency upgrade."],
                [FileCheck, "Quick eligibility check", "We assess grants and finance together to find the lowest possible cost for you."],
              ].map(([Icon, t, d]) => (
                <div key={t} className="flex gap-4">
                  <span className="w-11 h-11 rounded-xl bg-brand-cream border border-stone-200 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-brand-accent" />
                  </span>
                  <div>
                    <p className="font-outfit font-medium text-brand text-lg">{t}</p>
                    <p className="font-figtree text-stone-600">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/contact" data-testid="grants-cta-button" className="mt-8 inline-flex items-center gap-2 bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-7 py-3.5 font-medium">
              Check what I qualify for <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-stone-500 mt-4 font-figtree">*Grant availability and amounts are subject to eligibility and scheme rules. Finance is subject to status and affordability checks.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
